import { useState } from "react";
import { Button, Input, Label } from "@clevis/shared-ui";
import { call, CallError } from "../lib/callFunction";
import { SignaturePad } from "./SignaturePad";

// The customer signs the finished invoice on this device (the tech's phone
// or the office screen). It's kept on the invoice and printed on its PDF.

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

export function InvoiceSign({
  tenantId,
  invoiceId,
  number,
  totalCents,
  defaultName = "",
  onDone,
  onCancel,
}: {
  tenantId: string;
  invoiceId: string;
  number: number;
  totalCents: number;
  defaultName?: string;
  onDone: (signerName: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function sign() {
    if (!signature) return;
    setBusy(true);
    setError("");
    try {
      await call("signInvoice", { tenantId, invoiceId, signerName: name.trim(), signaturePngBase64: signature });
      onDone(name.trim());
    } catch (err) {
      const e = err as CallError;
      setError(e.hint ? `${e.message} ${e.hint}` : e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-steel/20 bg-cream/40 p-3">
      <p className="text-sm font-semibold text-ink">
        Customer signs invoice #{number} · {money(totalCents)}
      </p>
      <p className="text-xs text-steel">Hand the device to the customer. Signing says the work is done and the invoice is right.</p>
      <div>
        <Label>Customer's name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" autoComplete="off" />
      </div>
      <SignaturePad onChange={setSignature} />
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <div className="flex gap-2">
        <Button busy={busy} disabled={!signature || name.trim().length < 2} onClick={() => void sign()}>
          Sign
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
